import classNames from 'clsx';
import React, { useState } from 'react';
import type { Settings } from 'react-slick';
import Slider from 'react-slick';

import Arrow from './Arrow';
import styles from './Carousel.scss';
import Pagination from './Pagination';

interface Props {
  children: React.ReactElement | React.ReactElement[];
  sliderRef?: React.RefObject<Slider | null>;
  customArrowIcon?: React.ReactElement | SVGSVGElement;
  iconPosition?: 'inside' | 'outside';
  carouselSettings?: Settings;
  currentSlideCallback?: (curSlide: number) => void;
}

const Carousel: React.FC<Props> = ({
  sliderRef,
  children,
  carouselSettings,
  customArrowIcon,
  iconPosition,
  currentSlideCallback
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const childrenCount = Array.isArray(children) ? children.length : 1;

  const settings = React.useMemo(() => {
    const baseSettings = {
      autoplay: false,
      infinite: true,
      adaptiveHeight: true,
      beforeChange: (_: number, next: number) => {
        setCurrentSlide(next);
        if (currentSlideCallback) {
          currentSlideCallback(next);
        }
      },
      ...carouselSettings,
      dots: false
    };

    if (customArrowIcon) {
      baseSettings.prevArrow = (
        <Arrow arrowType="Previous" position={iconPosition}>
          {customArrowIcon}
        </Arrow>
      );
      baseSettings.nextArrow = (
        <Arrow arrowType="Next" position={iconPosition}>
          {customArrowIcon}
        </Arrow>
      );
    }

    return baseSettings;
  }, [carouselSettings, currentSlideCallback, iconPosition, customArrowIcon]);

  return (
    <div className={styles.carousel}>
      <Slider
        ref={sliderRef}
        className={classNames({
          [styles.customArrows]: customArrowIcon
        })}
        {...settings}
      >
        {children}
      </Slider>
      {carouselSettings?.dots && (
        <div className={styles.pagination}>
          <Pagination
            pagesCount={childrenCount}
            currentPageIndex={currentSlide}
            goToPage={(index) => sliderRef?.current?.slickGoTo(index)}
          />
        </div>
      )}
    </div>
  );
};

export { Carousel };
